import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IItemCreationHeader, ItemCreationSection } from '~/Components';
import { useProvider, useUser } from '~/Hooks';
import { GeneralInfo } from '~/Components/Icons/GeneralInfo';
import { INotificationSettings } from '~/Models/DTO/NotificationSettings.model';
import { UserProfileNotificationSettingsGeneralSection } from './UserProfileNotificationSettingsGeneralSection';
import { useNotificationSettings } from '~/requests';
import {
  extendedNotificationSettingsInitialValues,
  hasExtendedNotificationSettingsObjectChanged,
} from './Helpers/notificationSettingsHelpers';

export interface ExtendedNotificationSettings extends INotificationSettings {
  bookingMailCopyIntervalDaily: boolean;
  bookingMailCopyIntervalImmediately: boolean;
  checkoutMailCopyIntervalDaily: boolean;
  checkoutMailCopyIntervalImmediately: boolean;
  cancellationMailCopyIntervalDaily: boolean;
  cancellationMailCopyIntervalImmediately: boolean;
}

export const useUserProfileNotificationSettings = () => {
  // hooks
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { currentProvider } = useProvider();
  const {
    notificationSettings,
    updateNotificationSettings,
    isLoading: isNotificationSettingsLoading,
    hasError,
  } = useNotificationSettings();
  // state
  const [initialValues, setInitialValues] = useState<ExtendedNotificationSettings>(
    extendedNotificationSettingsInitialValues,
  );
  const [currentSetting, setCurrentSetting] = useState<ExtendedNotificationSettings>(
    extendedNotificationSettingsInitialValues,
  );

  const onSave = useCallback(() => {
    if (currentSetting) {
      updateNotificationSettings(currentSetting);
    }
  }, [currentSetting]);

  //compute
  const sections: ItemCreationSection<ExtendedNotificationSettings>[] = useMemo(
    () => [({ data }) => UserProfileNotificationSettingsGeneralSection(data)],
    [],
  );

  const headers: IItemCreationHeader[] = useMemo(
    () => [
      {
        index: 0,
        testId: 'profileTab.general',
        title: t('app.title.general'),
        icon: <GeneralInfo />,
      },
    ],
    [],
  );

  useEffect(() => {
    if (currentUser) {
      const backendNotificationSetting = notificationSettings.find(
        (setting) => setting.applicationUserId === currentUser.uuid,
      );
      if (backendNotificationSetting) {
        const extendedNotificationSettings = {
          ...backendNotificationSetting,
          bookingMailCopyIntervalDaily: backendNotificationSetting.bookingMailCopyInterval === 1,
          bookingMailCopyIntervalImmediately:
            backendNotificationSetting.bookingMailCopyInterval === 0,
          checkoutMailCopyIntervalDaily: backendNotificationSetting.checkoutMailCopyInterval === 1,
          checkoutMailCopyIntervalImmediately:
            backendNotificationSetting.checkoutMailCopyInterval === 0,
          cancellationMailCopyIntervalDaily:
            backendNotificationSetting.cancellationMailCopyInterval === 1,
          cancellationMailCopyIntervalImmediately:
            backendNotificationSetting.cancellationMailCopyInterval === 0,
        };
        setInitialValues(extendedNotificationSettings);
        setCurrentSetting(extendedNotificationSettings);
      }
    }
  }, [notificationSettings, currentUser?.uuid]);

  const getIntervalValueFromBoolean = (interval: boolean, value: boolean) => {
    if (interval === false) {
      return null;
    }
    return value ? 0 : 1;
  };

  const validate = (values: ExtendedNotificationSettings) => {
    const newValue = {
      ...values,
      applicationUserId: currentUser?.uuid,
      providerId: currentProvider?.uuid,
      bookingMailCopyInterval: getIntervalValueFromBoolean(
        values.receiveBookingMailCopy,
        values.bookingMailCopyIntervalDaily,
      ),
      checkoutMailCopyInterval: getIntervalValueFromBoolean(
        values.receiveCheckoutMailCopy,
        values.checkoutMailCopyIntervalDaily,
      ),
      cancellationMailCopyInterval: getIntervalValueFromBoolean(
        values.receiveCancellationMailCopy,
        values.cancellationMailCopyIntervalDaily,
      ),
    };
    setCurrentSetting(newValue);
    if (hasExtendedNotificationSettingsObjectChanged(currentSetting, newValue)) {
      updateNotificationSettings(newValue);
    }
  };

  return {
    listItems: sections,
    headers,
    onSave,
    initialValues,
    validate,
    error: hasError,
    isLoading: isNotificationSettingsLoading,
  };
};
